<script>
  import AudioPlayer from './AudioPlayer.svelte';
</script>

<!-- Section 8 -->
<section id="section-8" class="section-divider-ipg">
  <h3 class="heading-2-ipg">Section Seven</h3>
  <h3 class="heading-3-ipg">Conjugation of Verbs (<span class="italic-w-ipg">ākhyāta</span>)</h3>

  <p class="paragraph-1-ipg">
    Verbs are divided in Pāli into seven different groups or <a href="#section8-note-42" class="notes-ipg" id="section8-note-42-rtn">classes<sup class="sup-spacing-ipg">42</sup></a> according to the derivations and insertions their root undergoes when conjugated. The subsequent examples should <a href="#section8-note-43" class="notes-ipg" id="section8-note-43-rtn">suffice:<sup class="sup-spacing-ipg">43</sup></a>
  </p>
  <ul class="para-list-ipg">
    <li>For example the first group inserts <span class="italic-w-ipg">-a-</span> after the root and before the personal suffixes <span class="italic-w-ipg">-ti, -nti,</span> etc. Thus the example of the root: <span class="italic-w-ipg">gam-</span> is assimilated to <span class="italic-w-ipg">gac-ch-</span> and arranged under the first group, where always <span class="italic-w-ipg">-a-</span> gets inserted and added to the respective root: <span class="italic-w-ipg">gac-ch-a-ti</span>: <span class="italic-w-ipg">gacchati</span> (to go); <span class="italic-w-ipg">vas-a-ti</span>: <span class="italic-w-ipg">vasati</span> (to dwell); <span class="italic-w-ipg">har-a-ti</span>: <span class="italic-w-ipg">harati</span> (to carry); <span class="italic-w-ipg">jīv-a-ti</span>: <span class="italic-w-ipg">jīvati</span> (to live).</li>

    <li>Another class or group adds the suffix and then may use changes according to the rules of <span class="italic-w-ipg">sandhi</span>: <span class="italic-w-ipg">-ya-</span> to the root: <span class="italic-w-ipg">jā-ya-ti</span>: <span class="italic-w-ipg">jāyati</span> (to arise); <span class="italic-w-ipg">budh-ya-ti</span>: <span class="italic-w-ipg">bujjhati</span> (to know); <span class="italic-w-ipg">man-ya-ti</span>: <span class="italic-w-ipg">maññati</span> (to think, to imagine).</li>

    <li>Again another class inserts the <span class="italic-w-ipg">niggahita</span>: <span class="italic-w-ipg">-ṃ-</span> (which takes the form of the nasal sound according to the respective group) before the closing consonant of the root: <span class="italic-w-ipg">chi-ṃ-da-ti</span>: <span class="italic-w-ipg">chindati</span> (to cut); <span class="italic-w-ipg">mu-ṃ-ca-ti</span>: <span class="italic-w-ipg">muñcati</span> (to free); <span class="italic-w-ipg">li-ṃ-pa-ti</span>: <span class="italic-w-ipg">limpati</span> (to stain).</li>
  </ul>
  
  <p class="paragraph-1-ipg">
    Pāli differentiates between the following tenses, which have individual conjugational endings and inflections and are classified as:
  </p>
  
  <ul class="para-list-ipg">  
    <li><a href="#section8-note-44" class="notes-ipg" id="section8-note-44-rtn">three persons (third, second and first);<sup class="sup-spacing-ipg">44</sup></a></li>
    <li>two numbers: singular and plural;</li> 
    <li>six tenses: present (<span class="italic-w-ipg">gacchati</span>), <a href="#section8-note-45" class="notes-ipg" id="section8-note-45-rtn">imperfect,<sup class="sup-spacing-ipg">45</sup></a> <a href="#section8-note-46" class="notes-ipg" id="section8-note-46-rtn">aorist<sup class="sup-spacing-ipg">46</sup></a>(<span class="italic-w-ipg">agacchi</span>, altern: <span class="italic-w-ipg">agāma</span>; <span class="italic-w-ipg">agamī</span>; <span class="italic-w-ipg">agañchi</span>), <a href="#section8-note-47" class="notes-ipg" id="section8-note-47-rtn">perfect,<sup class="sup-spacing-ipg">47</sup></a> future (<span class="italic-w-ipg">gacchissati</span>), and conditional (<span class="italic-w-ipg">agacchissa</span>);</li>
    <li>causative (<span class="italic-w-ipg">gamete</span>; <span class="italic-w-ipg">gacchāpeti</span>);</li>
    <li>infinitive (<span class="italic-w-ipg">gantuṃ</span>; <span class="italic-w-ipg">gamitum</span>; <span class="italic-w-ipg">gantave</span>; <span class="italic-w-ipg">gamanaye</span>) and gerund (<span class="italic-w-ipg">gantvā</span>);</li>
    <li>participles: present (<span class="italic-w-ipg">gacchant</span>; <span class="italic-w-ipg">gacchamāna</span>), past (<span class="italic-w-ipg">gata</span>) and future passive (<span class="italic-w-ipg">gantabba</span>); </li>
    <li>three moods: indicative (<span class="italic-w-ipg">gacchati</span>), imperative (<span class="italic-w-ipg">gaccha</span>, 2nd person) and optative (<span class="italic-w-ipg">gaccheyyuṃ</span>); </li>
    <li>three voices: active, reflective and passive voice.</li>
  </ul>

  <!-- Example 1 -->
  <h4 id="section-8-1" class="subheading-1-ipg">1. The Persons</h4>
  <p class="paragraph-1-ipg">
    The three genders, masculine, neuter and feminine carry their individual personal endings in both numbers. In the active voice the primary endings (<span class="italic-w-ipg">ti; si; mi; nti; tha; -ma</span>) appear in the present indicative and in the future tense. The so called secondary endings (<span class="italic-w-ipg">t; s; aṃ; u/uṃ; tha; ma</span>) are used in the aorist and the conditional, the optative varies with both endings.
  </p>
  
  <!-- Example 2 -->
  <h4 id="section-8-2" class="subheading-1-ipg">2. The Tenses</h4>
  <ul class="para-list-ipg">  
    <li>The present tense describes an action that is taking place, an action that continues (equivalent to present progressive) or a fact. It is often used describing historical facts.</li>
    <li>The aorist, the historical or principal past is mostly used to denote the past tense. It always makes use of the secondary personal endings and is built in different ways, different forms may be used:</li>
    a) the root-aorist always prefixes the augment <span class="italic-w-ipg">a</span> to the root and uses the secondary personal ending: (<span class="italic-w-ipg">a-gam-t: agā; a-gam-s: agā; a-gam-m: agaṃ</span>)<br> 
    b) the a-aorist inserts the suffix <span class="italic-w-ipg">a</span> before the personal endings and mostly prefixes the augment <span class="italic-w-ipg">-a-</span>: (<span class="italic-w-ipg">a-gam-aṃ</span>: <span class="italic-w-ipg">agama</span>ṃ)<br>  
    c) the s-aorist inserts the suffix <span class="italic-w-ipg">s</span> before the personal secondary endings: (<span class="italic-w-ipg">a-gama-s-i-ṃ</span>: <span class="italic-w-ipg">agamāsiṃ</span>)<br> 
    d) the is-aorist inserts the suffix <span class="italic-w-ipg">is</span> before the personal secondary endings: (<span class="italic-w-ipg">a-gam-is-ṃ</span>: <span class="italic-w-ipg">agamiṃ / gacch-is-ṃ: gacchiṃ / gañchiṃ</span>)
    <li>The future tense expresses futurity and is formed by adding <em>ssa</em> to the root or verbal base with, or in some cases without, the connecting vowel <span class="italic-w-ipg">i</span>; the terminations are the same as those in the present tense: <span class="italic-w-ipg">gacch-i-ssa-mi</span>: <span class="italic-w-ipg">gacchissāmi</span>. The future tense can also express a command or condition.</li>
    <li>The conditional expresses a possibility or wish. It uses the secondary personal endings and adds the augment <span class="italic-w-ipg">a</span> before the future forms: <span class="italic-w-ipg">a-gacch-iss-aṃ</span>; <span class="italic-w-ipg">a-gacch-iss-a</span>. </li>
    <li>The causative prompts something to happen and is formed by adding either the suffix <span class="italic-w-ipg">aya</span> or <span class="italic-w-ipg">e</span> to the root, or alternatively inserts <span class="italic-w-ipg">paya</span> or <span class="italic-w-ipg">pe</span>: <span class="italic-w-ipg">gameti, gāmayati, gacchāpeti, gamāpeti</span>.</li>
  </ul>
  
  <!-- Example 3 -->
  <h4 id="section-8-3" class="subheading-1-ipg">3. The Moods</h4>
  <ul class="para-list-ipg">  
    <li>The indicative mood expresses an action, describes an occasion or occurrence. The personal primary endings are added to the stem of the present tense.</li>
    <li>The optative expresses mainly probability and advice, wishes and ideas. It is formed by adding <span class="italic-w-ipg">eyya</span> to the verbal base before personal endings. When translated: - if, might, would, etc. - may be used to convey the sense of it.</li>
    <li>The imperative mood expresses commands, benediction or wishes. It is mainly used in the active voice with the following personal endings: <span class="italic-w-ipg">tu; hi; ntu;</span> <span class="italic-w-ipg">tha</span> for the 3rd and 2nd. <a href="#section8-note-48" class="notes-ipg" id="section8-note-48-rtn">These are added to the stem.<sup class="sup-spacing-ipg">48</sup></a></li>
  </ul>
  
  <!-- Example 4 -->
  <h4 id="section-8-4" class="subheading-1-ipg">4. Infinitive, Gerund and Participles</h4>
  <ul class="para-list-ipg"> 
    <li>The infinitive can be used actively and passively and denotes purpose and intention. It gets built by adding the suffix <span class="italic-w-ipg">tum</span> to the respective roots: <span class="italic-w-ipg">gantuṃ</span> (to go). Further especially with verbs of frequent occurrence the following suffixes are added as well: <span class="italic-w-ipg">itum, tave, tuye</span>. </li>
    <li>The gerund describes the completion of an action: <span class="italic-w-ipg">gantvā</span> (having gone). It also expresses the perpetuation of activity and thus connects different activities. The suffix <span class="italic-w-ipg">tvā</span> is added to the root of the verb or verbal base with or sometimes without the connecting vowel <span class="italic-w-ipg">i</span> to form the gerund. </li>
    <li>Present participles are formed by adding <span class="italic-w-ipg">nta</span>; <span class="italic-w-ipg">ntī</span> or <span class="italic-w-ipg">māna</span>; <span class="italic-w-ipg">mānā</span> to the <a href="#section8-note-49" class="notes-ipg" id="section8-note-49-rtn">verbal base.<sup class="sup-spacing-ipg">49</sup></a> They function as adjectives and agree in gender, number and case with the nouns they qualify. They are declined like the respective nouns and may be translated with the help of: ‘while’: <span class="italic-w-ipg">gacchanta</span>; <span class="italic-w-ipg">gacchant</span> – (while) going.</li>
    <li>Past participles can take an active meaning or a passive meaning. The active past participles add the suffixes <span class="italic-w-ipg">vant</span>, <span class="italic-w-ipg">vin</span> to the root. It can be translated as <span class="italic-w-ipg">vusitavant</span> (one who has lived). The passive perfect participles are formed by adding <span class="italic-w-ipg">ta</span> <span class="italic-w-ipg">na</span> to the root, with or without the connecting vowel <span class="italic-w-ipg">i</span> and are translated like the English past perfect participles <span class="italic-w-ipg">gacchita gata gamita</span> (gone). Past participles have a passive meaning when they are formed from transitive verbs, but from intransitive verbs they have an active meaning.</li> 
  </ul>

  <div class="paragraph-1-ipg" id="for-example-cont">
    <p>
    For example: <span class="italic-w-ipg">gacchati, tiṭṭhati</span> are intransitive verbs. Therefore,<br>
        <span class="italic-w-ipg">puriso gato</span> (the man has gone - active meaning),<br>
        <span class="italic-w-ipg">vanitā ṭhitā</span> (the woman has stood - active meaning),<br>
    </p>
    <p>
    <span class="italic-w-ipg">pacati, dadāti,</span> are transitive verbs, <br>
        <span class="italic-w-ipg">odano pacito</span> (the rice is cooked - passive meaning),<br>
        <span class="italic-w-ipg">mettā datā</span> (<span class="italic-w-ipg">metta</span> is given - passive meaning).<br>
    </p>
  </div>
  
<p class="paragraph-1-ipg">
  Future passive participles or potential future participles express something that has to be done or ideally should be done. The suffixes <span class="italic-w-ipg">tabba</span> / <span class="italic-w-ipg">anīya</span> / <span class="italic-w-ipg">ya</span> get added to the verbal root, with or without the connecting vowel <span class="italic-w-ipg">iy</span>. Thus the root <span class="italic-w-ipg">kar</span> (to do) can develop the following forms (should be done): <span class="italic-w-ipg">kar-tabba</span>: <span class="italic-w-ipg">kātabba/kattabba; kar-aniya: karaṇiya; kar-ya: kariya: kayira;</span> (Sanskrit: <span class="italic-w-ipg">kṛtya</span>) (with insertion of t): <span class="italic-w-ipg">kicca</span>. Future passive participles carry more optative or imperative meaning and are declined along with the respective nouns <span class="italic-w-ipg">kammaṃ kattabbaṃ</span> (the work should be performed).
</p>


<h4 id="section-8-5" class="subheading-1-ipg">5. The Three Voices</h4>
<ul class="para-list-ipg"> 
  <li>The active voice is the form that is of common occurrence. The word for the active voice in Pāli is <span class="italic-w-ipg">parassapada</span> – (lit. word expressing action on others) and thus describes the result or consequence of an action that is produced on others by this very action. It is also called <span class="italic-w-ipg">kattukāraka</span>.</li>
  <li>The reflexive or middle voice is rarely used and mostly found in poetry. The word for the middle or reflexive voice in Pāli is <span class="italic-w-ipg">attanopada</span> (lit. word expressing action on oneself) and thus describes the result or consequence of an action that is produced on the agent himself by this very action. It is also called <a href="#section8-note-50" class="notes-ipg" id="section8-note-50-rtn"><span class="italic-w-ipg">kammakāraka</span>.<sup class="sup-spacing-ipg">50</sup></a> The primary endings of the reflexive voice are <span class="italic-w-ipg">te, se, e, nte, vhe, mhe</span>. The secondary endings are <span class="italic-w-ipg">tha, tho, m/aṃ, re/raṃ, vho, mhase</span>. Because of its rare occurrence the respective conjugations are neglected here.</li> 
  <li>The passive voice adds the suffix <span class="italic-w-ipg">ya</span> to transitive verbs so they can adopt passive meaning: <span class="italic-w-ipg">gacchīyati</span>  (to be gone to); <span class="italic-w-ipg">dīyati</span> (to be given). </li>
</ul>

<p class="paragraph-1-ipg">
  In general the terminations of the verbs remain the same throughout the different groups and in the process of reading the suttas one gets easily accustomed to these tenses. For the purpose of the collection as presented in <a href="https://learning.pariyatti.org/course/view.php?id=2" target="_blank">‘Exploring the Path’</a> while reading suttas and complete sentences it becomes easy to understand those usages without needing to know all the grammatical details. The subsequent tables should suffice for the purpose of this introduction.
</p>

<h4 id="section-8-6" class="subheading-1-ipg">6. Table Survey: present indicative, imperative, optative and future</h4>
<!-- table 7, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Indicative</th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Imperative</th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Optative</th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Future</th>
        <th class="list-chart-4-ipg"></th>
      </tr>
      <tr>								
        <th class="list-chart-3-ipg">3rd sing.</th>
        <td class="list-chart-1-ipg">sā\so</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ti</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchati</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-tu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchatu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyya</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyya</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issati</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissati</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd sing.</th>
        <td class="list-chart-1-ipg">tvaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-si</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchasi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-hi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchahi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyyāsi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyyāsi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issasi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissasi</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st sing.</th>
        <td class="list-chart-1-ipg">ahaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-mi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchāmi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-mi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchāmi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyyaṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyyaṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issāmi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissāmi</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">3rd plural</th>
        <td class="list-chart-1-ipg">te</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-nti</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchanti</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ntu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchantu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyyuṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyyuṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issanti</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissanti</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd plural</th>
        <td class="list-chart-1-ipg">tumhe</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-tha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchatha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-tha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchatha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyyātha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyyātha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issatha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissatha</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st plural</th>
        <td class="list-chart-1-ipg">mayaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-eyyāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaccheyyāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-issāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchissāma</span></td>
      </tr>
    </table>
  </div>
  <!-- /table -->


  <h4 id="section-8-7" class="subheading-1-ipg">7. Table Survey: different forms of aorist</h4>
<!-- table 8, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Sec. End</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">Root - Aorist</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">a - Aorist</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">s - Aorist</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">is - Aorist</th>
      </tr>
      <tr>								
        <th class="list-chart-3-ipg">3rd sing.</th>
        <td class="list-chart-1-ipg">sā\so</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-t</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agā</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agama</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamāsi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchi/agami</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd sing.</th>
        <td class="list-chart-1-ipg">tvaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-s</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agā</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agama</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamāsi</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchi/agami</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st sing.</th>
        <td class="list-chart-1-ipg">ahaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-m/aṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">a-<br>gam</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agaṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">a-<br>gam<br>a-<br>m</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamaṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamā<br>s-<br>iṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamāsiṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacch-<br>is-m /<br>agam-<br>is-m</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchiṃ /<br>agamiṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">3rd plural</th>
        <td class="list-chart-1-ipg">te</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">u/uṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">aguṃ<br>agu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamuṃ</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamaṃsu</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchisuṃ / <br>agamisuṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd plural</th>
        <td class="list-chart-1-ipg">tumhe</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">tha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span>/td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamatha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamattha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchittha / <br>agamittha</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st plural</th>
        <td class="list-chart-1-ipg">mayaṃ</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">ma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamāma</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">agamamha</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchimha / <br>agamimha</span></td>
      </tr>
    </table>
  </div>
  <!-- /table -->
  
  <h4 id="section-8-8" class="subheading-1-ipg">8. Table Survey: gerund, present participle, past participle, future passive participle</h4>
<!-- table 9, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">Gerund</th>
        <th class="list-chart-4-ipg">Translation</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">Present Participle</th>
        <th class="list-chart-4-ipg">Translation</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">Past Participle</th>
        <th class="list-chart-4-ipg">Translation</th>
        <th class="list-chart-4-ipg">Form</th>
        <th class="list-chart-4-ipg">Future Participle</th>
        <th class="list-chart-4-ipg">Translation</th>
      </tr>
      <tr>								
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">-tvā</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gantvā</span></td>
        <td class="list-chart-1-ipg">having gone</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-nta</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchanta</span></td>
        <td class="list-chart-1-ipg">going</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ta</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gata</span></td>
        <td class="list-chart-1-ipg">gone</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-tabba</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gantabba</span></td>
        <td class="list-chart-1-ipg">to be gone</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-1-ipg">datvā</td>
        <td class="list-chart-1-ipg">having given</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dadanta</span></td>
        <td class="list-chart-1-ipg">giving</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">datta</span></td>
        <td class="list-chart-1-ipg">given</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dātabba</span></td>
        <td class="list-chart-1-ipg">to be given</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pacitvā</span></td>
        <td class="list-chart-1-ipg">having cooked</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pacanta</span></td>
        <td class="list-chart-1-ipg">cooking</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ita</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pacita</span></td>
        <td class="list-chart-1-ipg">cooked</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhuñjitabba</span></td>
        <td class="list-chart-1-ipg">to be enjoyed</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg">-ya</td>
        <td class="list-chart-1-ipg">āgamma</td>
        <td class="list-chart-1-ipg">having come</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-māna</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchamāna</span></td>
        <td class="list-chart-1-ipg">going</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gacchita</span></td>
        <td class="list-chart-1-ipg">gone</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-anīya</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gamanīya</span></td>
        <td class="list-chart-1-ipg">to be gone</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-1-ipg">ādāya</td>
        <td class="list-chart-1-ipg">having given</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pacamāna</span></td>
        <td class="list-chart-1-ipg">cooking</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-na</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dinna</span></td>
        <td class="list-chart-1-ipg">given</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dassanīya</span></td>
        <td class="list-chart-1-ipg">to be seen</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-1-ipg">āruyha</td>
        <td class="list-chart-1-ipg">having climbed</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhuñjamāna</span></td>
        <td class="list-chart-1-ipg">eating</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">nisinna</span></td>
        <td class="list-chart-1-ipg">seated</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">-ya</span></td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhuñjiya</span></td>
        <td class="list-chart-1-ipg">to be enjoyed</td>
      </tr>
    </table>
  </div>
  <!-- /table -->
  
  <h4 id="section-8-9" class="subheading-1-ipg">9. Further examples of past participles	</h4>

  <AudioPlayer audioURL={"/10-Past-Participles-page-28.mp3"} />		

  <!-- table 9, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>								
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āmasati</span></td>
        <td class="list-chart-1-ipg">to rub</td>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">āmasita, āmaṭṭha</span></td>
        <td class="list-chart-1-ipg">rubbed</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">ārabhati</span></td> 	
        <td class="list-chart-1-ipg">to start</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">āraddha</span></td>			
        <td class="list-chart-1-ipg">started</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">āsiñcati</span></td> 		
        <td class="list-chart-1-ipg">to sprinkle </td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">āsitta</span></td>			
        <td class="list-chart-1-ipg">sprinkled</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhavati</span></td> 		
        <td class="list-chart-1-ipg">to become</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhūta</span></td>			
        <td class="list-chart-1-ipg">become</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhuñjati</span></td> 	
        <td class="list-chart-1-ipg">to enjoy</td>     
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">bhuñjita, bhutta</span></td>
        <td class="list-chart-1-ipg">enjoyed</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">cavati</span></td> 		
        <td class="list-chart-1-ipg">to fall away</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">cuta</span></td>			 
        <td class="list-chart-1-ipg">fell away</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dadāti</span></td> 		
        <td class="list-chart-1-ipg">to give</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dinna</span></td>			
        <td class="list-chart-1-ipg">given</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">ḍasati</span></td> 		
        <td class="list-chart-1-ipg">to bite</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">daṭṭha</span></td>			
        <td class="list-chart-1-ipg">bitten</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dhovati</span></td> 		
        <td class="list-chart-1-ipg">to wash</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">dhovita, dhota</span>	</td>      		
        <td class="list-chart-1-ipg">washed</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gaṇhāti</span></td> 		
        <td class="list-chart-1-ipg">to seize</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">gahita</span></td>			 
        <td class="list-chart-1-ipg">seized, held</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">hanati</span></td> 		
        <td class="list-chart-1-ipg">to kill</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">hata</span></td>			
        <td class="list-chart-1-ipg">killed</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">harati</span></td> 		
        <td class="list-chart-1-ipg">to take away</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">haṭa</span></td>			 
        <td class="list-chart-1-ipg">took away</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">jānāti</span></td> 		
        <td class="list-chart-1-ipg">to know</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">ñāta</span></td>			
        <td class="list-chart-1-ipg">known</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">karoti</span></td> 		
        <td class="list-chart-1-ipg">to do</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kata</span></td>			
        <td class="list-chart-1-ipg">done</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kasati</span></td> 		
        <td class="list-chart-1-ipg">to plough</td>       
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kasita, kaṭṭha</span>	</td>          		
        <td class="list-chart-1-ipg">ploughed</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">khipati</span></td> 		
        <td class="list-chart-1-ipg">to throw</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">khitta</span></td>			
        <td class="list-chart-1-ipg">thrown</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kiṇāti</span></td> 		
        <td class="list-chart-1-ipg">to buy</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kīta</span></td>			
        <td class="list-chart-1-ipg">bought</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kujjhati</span></td> 		
        <td class="list-chart-1-ipg">to be angry</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">kuddha</span></td>			 
        <td class="list-chart-1-ipg">was angry</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">labhati</span></td> 		
        <td class="list-chart-1-ipg">to gain</td>          
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">laddha, labhita</span></td>		
        <td class="list-chart-1-ipg">gained</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">mināti</span></td> 		
        <td class="list-chart-1-ipg">to measure</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">mita</span></td>			
        <td class="list-chart-1-ipg">measured</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">muñcati</span></td>
        <td class="list-chart-1-ipg">to deliver, emit</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">mutta</span></td>			
        <td class="list-chart-1-ipg">delivered</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">nikkhamati</span></td> 	
        <td class="list-chart-1-ipg">to leave</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">nikkhanta</span> </td>			 
        <td class="list-chart-1-ipg">gone out, left</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pāpuṇāti</span></td> 	
        <td class="list-chart-1-ipg">to reach</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">patta</span></td>			
        <td class="list-chart-1-ipg">reached, attained</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pacati</span></td> 		
        <td class="list-chart-1-ipg">to cook</td>	        
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pācita, pakka</span></td>	
        <td class="list-chart-1-ipg">cooked</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pajahati</span></td> 	
        <td class="list-chart-1-ipg">to abandon</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pahīna</span></td>			
        <td class="list-chart-1-ipg">abandoned</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pasīdati</span></td>			
        <td class="list-chart-1-ipg">to become bright</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pasanna</span></td>			
        <td class="list-chart-1-ipg">brightened</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pavisati</span></td> 		
        <td class="list-chart-1-ipg">to enter</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">paviṭṭha</span> </td>			
        <td class="list-chart-1-ipg">entered</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">phusati</span></td>
        <td class="list-chart-1-ipg">to touch</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">phuṭṭa</span></td>			
        <td class="list-chart-1-ipg">touched</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pivati</span></td> 		
        <td class="list-chart-1-ipg">to drink</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pīta</span></td>			
        <td class="list-chart-1-ipg">drunk</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pucchati</span></td> 	
        <td class="list-chart-1-ipg">to ask</td>	 
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">pucchita, puṭṭha</span></td>
        <td class="list-chart-1-ipg">asked</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">suṇāti</span></td> 		
        <td class="list-chart-1-ipg">to hear</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">suta</span></td>			
        <td class="list-chart-1-ipg">heard</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">tiṭṭhati</span></td> 		
        <td class="list-chart-1-ipg">to stand</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">ṭhita</span></td>			
        <td class="list-chart-1-ipg">stood</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vapati</span></td> 		
        <td class="list-chart-1-ipg">to sow</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vutta</span></td>			
        <td class="list-chart-1-ipg">sown</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vasati</span></td> 		
        <td class="list-chart-1-ipg">to live</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vuttha</span></td>			 
        <td class="list-chart-1-ipg">lived, dwelled</td>
      </tr>
      <tr>
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vivarati</span></td> 		
        <td class="list-chart-1-ipg">to uncover</td>	
        <td class="list-chart-1-ipg"><span class="italic-w-ipg">vivaṭa</span></td>			 
        <td class="list-chart-1-ipg">uncovered, opened</td>
      </tr>  
    </table>
  </div>
  <!-- /table -->


  <h4 id="section-8-10" class="subheading-1-ipg">10. Table Survey: special conjugation: <span class="italic-w-ipg">atthi</span> – to be</h4>
<!-- table 10, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Indicative</th>
        <th class="list-chart-4-ipg">Aorist</th>
        <th class="list-chart-4-ipg">Optative</th>
        <th class="list-chart-4-ipg">Imperative</th>
        <th class="list-chart-4-ipg">Future</th>
      </tr>
      <tr>								
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">3rd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">sā\so</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">atthi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assa, siyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">atthu</span></td>
        <td class="list-chart-3-ipg"></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">2nd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assa, siyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āhi</span></td>
        <td class="list-chart-3-ipg">see: <span class="italic-w-ipg">bhavati</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st sing.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asmi, amhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsiṁ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assaṃ, siyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asmi, amhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">3rd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">santi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsuṃ, āsiṃsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assu, siyuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">santu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhe</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">attha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsittha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assatha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">attha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st  pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asma, amha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">āsimha, āsimhā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">assāma</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asma, amha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
      </tr>
    </table>
  </div>
  <!-- /table -->

  <h4 id="section-8-11" class="subheading-1-ipg">11. Table Survey: special conjugation: <span class="italic-w-ipg">bhavati</span> – to be, to exist, to become</h4>
<!-- table 11, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Indicative</th>
        <th class="list-chart-4-ipg">Aorist</th>
        <th class="list-chart-4-ipg">Optative</th>
        <th class="list-chart-4-ipg">Imperative</th>
        <th class="list-chart-4-ipg">Future</th>
      </tr>
      <tr>								
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">3rd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">sā\so</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavati</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahū, ahu, (bhavi)</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhaveyya, bhave</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavatu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissati</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">2nd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavasi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahū, ahu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhaveyyāsi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhava, bhavāhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissasi</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st sing.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavāmi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhaveyyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissāmi</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">3rd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavanti</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhaveyyuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavantu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissanti</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhe</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavatha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavetha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavatha, bhavātha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissatha</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st  pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavāma</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahumha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhaveyyāma, bhavema</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavissāma</span></td>
      </tr>
    </table>
  </div>
  <!-- /table -->

  <h4 id="section-8-12" class="subheading-1-ipg">12. Table Survey: special conjugation: <span class="italic-w-ipg">hoti</span> to be, to exist, to become 	(root <italic-w-ipg>bhū</italic-w-ipg>- reduced to <italic-w-ipg>hū</italic-w-ipg>-)</h4>
<!-- table 12, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Indicative</th>
        <th class="list-chart-4-ipg">Aorist</th>
        <th class="list-chart-4-ipg">Optative</th>
        <th class="list-chart-4-ipg">Imperative</th>
        <th class="list-chart-4-ipg">Future</th>
      </tr>
      <tr>								
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">3rd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">sā\so</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hoti</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahosi, ahū</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hotu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hohiti, hessati, hossati </span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg"><span class="italic-w-ipg">2nd sing.</span></th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hosi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahosi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyyāsi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hohi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hessasi, hohisi</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st sing.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">homi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahosiṃ, ahuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">homi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hohāmi, hessāmi, hessaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">3rd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">honti</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahesuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyyuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hontu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hessanti, hohinti</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">2nd pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhe</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hotha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahosittha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyyātha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hotha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hessatha, hohitha</span></td>
      </tr>
      <tr>
        <th class="list-chart-3-ipg">1st  pl.</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">homa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahesuṃha</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">huveyyāma</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">homa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hessāma, hohāma</span></td>
      </tr>
    </table>
  </div>
  <!-- /table -->

  <h4 id="section-8-13" class="subheading-1-ipg">13.	Table Survey: gerund, present participle, past participle, future passive participle, infinitive of <span class="italic-w-ipg">atthi</span> and <span class="italic-w-ipg">bhavati</span>/<span class="italic-w-ipg">hoti</span></h4>
<!-- table 13, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">
      <tr>
        <th class="list-chart-4-ipg">Verbs</th>
        <th class="list-chart-4-ipg">Gerund</th>
        <th class="list-chart-4-ipg">Transl.</th>
        <th class="list-chart-4-ipg">Pres. p.</th>
        <th class="list-chart-4-ipg">Transl.</th>
        <th class="list-chart-4-ipg">Past p.</th>
        <th class="list-chart-4-ipg">Transl.</th>
        <th class="list-chart-4-ipg">Fut. pass.p.</th>
        <th class="list-chart-4-ipg">Transl.</th>
        <th class="list-chart-4-ipg">Infinitive</th>
        <th class="list-chart-4-ipg">Transl.</th>    
      </tr>
      <tr>								
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">atthi</span></td>
        <td class="list-chart-3-ipg">see:<span class="italic-w-ipg">bhavati</span> and <span class="italic-w-ipg">hoti</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">santa, samāna</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">being</span></td>
        <td class="list-chart-3-ipg">see: <span class="italic-w-ipg">bhavati</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
      </tr>    
      <tr>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavati</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavitvā</span></td>
        <td class="list-chart-3-ipg">having become</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavaṃ, bhavanta</span></td>
        <td class="list-chart-3-ipg">becoming</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhūta</span></td>
        <td class="list-chart-3-ipg">become</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavitabba, bhabba</span></td>
        <td class="list-chart-3-ipg">should be, could be</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">bhavituṃ</span></td>
        <td class="list-chart-3-ipg">to become, to exist, to be</td>
      </tr>
      <tr>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hoti</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hutvā, hutvāna</span></td>
        <td class="list-chart-3-ipg">having been</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">honta, hontī</span></td>
        <td class="list-chart-3-ipg">being</td>
        <td class="list-chart-3-ipg">see: <span class="italic-w-ipg">bhavati</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg"></span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hotabba, bhuyya</span></td>
        <td class="list-chart-3-ipg">should be, could be</td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">hotuṃ</span></td>
        <td class="list-chart-3-ipg">to become, to exist, to be</td>
      </tr>
    </table>
  </div>
  <!-- /table -->


  <!-- footnotes section 8 -->
  <ul class="footnotes-container-ipg">
    <li id="section8-note-42" class="footnotes-ipg"><sup>42</sup>Some groups are even subdivided into more than seven.  <a class="backarrow-ipg" href="#section8-note-42-rtn">↩</a></li>
    <li id="section8-note-43" class="footnotes-ipg"><sup>43</sup>For detailed analyses of how the various classes of verbs are developed and subdivided, refer to Perniola's and Warder’s <em>Pāli Grammar</em>.  <a class="backarrow-ipg" href="#section8-note-43-rtn">↩</a></li>
    <li id="section8-note-44" class="footnotes-ipg"><sup>44</sup>This traditional order demonstrates the respect that is held towards the other person rather than that the first person precedes the others in its position. In Pāli the 3rd is called <span class="italic-w-ipg">paṭhama purisa</span> (first person), the 2nd <span class="italic-w-ipg">majjhima purisa</span> (middle person) and the 1st <span class="italic-w-ipg">uttama purisa</span> (last person). In modern languages ‘I’ precedes as first person.  <a class="backarrow-ipg" href="#section8-note-44-rtn">↩</a></li>
    <li id="section8-note-45" class="footnotes-ipg"><sup>45</sup>Imperfect in Pāli expresses a definite past.  <a class="backarrow-ipg" href="#section8-note-45-rtn">↩</a></li>
    <li id="section8-note-46" class="footnotes-ipg"><sup>46</sup>Aorist in Pāli expresses past action, the time that recently past; it is the true past in the text. Although the present tense is often used to open the text (historical present) describing the historical background (<span class="italic-w-ipg">viharati</span> – dwells): -<span class="italic-w-ipg">ekaṃ samayaṃ bhagavā rājagahe viharati …… ānando …… nivāsetvā …… rājagahaṃ piṇḍāya pāvisi.),</span> and then continues in the aorist (<span class="italic-w-ipg">pāvisi</span> – went), both are generally translated in the past tense.   <a class="backarrow-ipg" href="#section8-note-46-rtn">↩</a></li>
    <li id="section8-note-47" class="footnotes-ipg"><sup>47</sup>Perfect in Pāli expresses indefinite past and is rarely used.  <a class="backarrow-ipg" href="#section8-note-47-rtn">↩</a></li>
    <li id="section8-note-48" class="footnotes-ipg"><sup>48</sup> The personal forms in all the tenses force stems and roots at times to undergo certain changes according to the <span class="italic-w-ipg">sandhi</span> rules by assimilation, adaption, elision. (See  'Word Formation'.) <a class="backarrow-ipg" href="#section8-note-48-rtn">↩</a></li>
    <li id="section8-note-49" class="footnotes-ipg"><sup>49</sup>The suffix <span class="italic-w-ipg">nta</span> is used in the active voice, <span class="italic-w-ipg">māna</span> in the reflective voice, but both get mainly translated in the same way.  <a class="backarrow-ipg" href="#section8-note-49-rtn">↩</a></li>
    <li id="section8-note-50" class="footnotes-ipg"><sup>50</sup> <span class="italic-w-ipg">kattukāraka</span>: the relation to the action as agent, being an agent; <span class="italic-w-ipg">kammakāraka</span>:  the relation to the action as patient, being an patient. <a class="backarrow-ipg" href="#section8-note-50-rtn">↩</a></li>
  </ul>
</section>
<!-- /Section 8-->


<style>
</style>